import roadmapManualApi from '@/api/roadmapManual';
import {mutationTypes} from '@/store/modules/roadmapManual/mutations';

import {getFormattedDate} from '@/helpers/utils';

export const actionsTypes = {
  getIface: '[roadmap-manual] get iface',
  getList: '[roadmap-manual] get list',
  getTasksStat: '[roadmap-manual] get tasks stat',
  createRoadmap: '[roadmap-manual] create roadmap',
  createGroupRoadmaps: '[roadmap-manual] create group roadmaps',
  proposeRoadmap: '[roadmap-manual] propose roadmap',
  getProposeAvailability: '[roadmap-manual] get propose availability',
  getDirectSupervisors: '[roadmap-manual] get direct supervisors',
  getHeader: '[roadmap-manual] get header',
  updateHeader: '[roadmap-manual] update header',
  deleteRoadmap: '[roadmap-manual] delete roadmap',
  changeStatus: '[roadmap-manual] change status',
  getDecisions: '[roadmap-manual] get decisions',
  getCommonMaterials: '[roadmap-manual] get common materials',
  getBlockMaterials: '[roadmap-manual] get block materials',
  getSkills: '[roadmap-manual] get skills',
  addMaterials: '[roadmap-manual] add materials',
  deleteMaterials: '[roadmap-manual] delete materials',
  updateMaterial: '[roadmap-manual] update material',
  changeMaterialStatus: '[roadmap-manual] change material status',
  saveMaterialResult: '[roadmap-manual] save material result',
  getMaterialComments: '[roadmap-manual] get material comments',
  addMaterialComment: '[roadmap-manual] add material comment',
  getSupervised: '[roadmap-manual] get supervised',
  getSupervisedBadge: '[roadmap-manual] get supervised badge',
};

const actions = {
  [actionsTypes.getIface](context, companyId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getIfaceStart);
      roadmapManualApi
        .getIface(companyId)
        .then((response) => {
          context.commit(mutationTypes.getIfaceSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getIfaceFailure, result?.response?.data?.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getList](
    context,
    {companyId, filters = {}, pagination: {limit = 10, offset = 0}, sort: {sort_field, sort_order}}
  ) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getListStart);

      const args = {};

      if (filters.users && filters.users.length) {
        args.user_ids = filters.users.map((u) => u.user_id);
      }
      if (filters.teamIds && filters.teamIds.length) {
        args.team_ids = filters.teamIds;
      }
      if (filters.teamGroupIds && filters.teamGroupIds.length) {
        args.team_group_ids = filters.teamGroupIds;
      }
      if (filters.goalTypeCodes && filters.goalTypeCodes.length) {
        args.goal_type_codes = filters.goalTypeCodes;
      }
      if (filters.reasonCodes && filters.reasonCodes.length) {
        args.reason_codes = filters.reasonCodes;
      }
      if (filters.sourceCodes && filters.sourceCodes.length) {
        args.source_codes = filters.sourceCodes;
      }
      if (filters.specCodes && filters.specCodes.length) {
        args.spec_codes = filters.specCodes;
      }
      if (filters.skillType) {
        args.skill_type = filters.skillType;
      }
      if (filters.statuses && filters.statuses.length) {
        args.statuses = filters.statuses;
      }
      if (filters.period?.dateStart && filters.period?.dateEnd) {
        args.start_date = getFormattedDate(filters.period.dateStart);
        args.end_date = getFormattedDate(filters.period.dateEnd);
      }
      if (filters.mentors && filters.mentors.length) {
        args.mentor_ids = filters.mentors.map((m) => m.user_id);
      }
      if (filters.hasOverdue !== null && filters.hasOverdue !== undefined) {
        args.has_overdue = filters.hasOverdue;
      }

      roadmapManualApi
        .getList(companyId, {
          filters: args,
          pagination: {limit, offset},
          sort: {sort_field, sort_order},
        })
        .then((response) => {
          context.commit(mutationTypes.getListSuccess, {...response.data, offset});
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getListFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getTasksStat](context, {roadmapId, sourceCode}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getTasksStatStart);
      roadmapManualApi
        .getTasksStat(roadmapId, sourceCode)
        .then((response) => {
          context.commit(mutationTypes.getTasksStatSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getTasksStatFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.createRoadmap](context, data) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.createRoadmapStart);
      roadmapManualApi
        .createRoadmap(data)
        .then((response) => {
          context.commit(mutationTypes.createRoadmapSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          const data = result.response && result.response.data;
          const error = (data && (data.message || data.error)) || result.message;

          context.commit(mutationTypes.createRoadmapFailure, error);
          reject(result);
        });
    });
  },
  [actionsTypes.createGroupRoadmaps](context, data) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.createGroupRoadmapsStart);
      roadmapManualApi
        .createGroupRoadmaps(data)
        .then((response) => {
          context.commit(mutationTypes.createGroupRoadmapsSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          const data = result.response && result.response.data;
          const error = (data && (data.message || data.error)) || result.message;

          context.commit(mutationTypes.createGroupRoadmapsFailure, error);
          reject(result);
        });
    });
  },
  [actionsTypes.proposeRoadmap](context, data) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.proposeRoadmapStart);
      roadmapManualApi
        .proposeRoadmap(data)
        .then((response) => {
          context.commit(mutationTypes.proposeRoadmapSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.proposeRoadmapFailure, result?.response?.data?.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getProposeAvailability](context, companyId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getProposeAvailabilityStart);
      roadmapManualApi
        .getProposeAvailability(companyId)
        .then((response) => {
          context.commit(mutationTypes.getProposeAvailabilitySuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getProposeAvailabilityFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getDirectSupervisors](context, {companyId, userIds}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getDirectSupervisorsStart);

      const args = {
        company_id: companyId,
        user_ids: userIds.join(','),
      };

      roadmapManualApi
        .getDirectSupervisors(args)
        .then((response) => {
          context.commit(mutationTypes.getDirectSupervisorsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getDirectSupervisorsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getHeader](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getHeaderStart);
      roadmapManualApi
        .getHeader(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.getHeaderSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getHeaderFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.updateHeader](context, {roadmapId, data}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.updateHeaderStart);
      roadmapManualApi
        .updateHeader(roadmapId, data)
        .then((response) => {
          context.commit(mutationTypes.updateHeaderSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.updateHeaderFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.deleteRoadmap](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.deleteRoadmapStart);
      roadmapManualApi
        .deleteRoadmap(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.deleteRoadmapSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.deleteRoadmapFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.changeStatus](context, {roadmapId, data}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.changeStatusStart);
      roadmapManualApi
        .changeStatus(roadmapId, data)
        .then((response) => {
          context.commit(mutationTypes.changeStatusSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.changeStatusFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getDecisions](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getDecisionsStart);
      roadmapManualApi
        .getDecisions(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.getDecisionsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getDecisionsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getCommonMaterials](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getCommonMaterialsStart);
      roadmapManualApi
        .getCommonMaterials(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.getCommonMaterialsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getCommonMaterialsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getBlockMaterials](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getBlockMaterialsStart);
      roadmapManualApi
        .getBlockMaterials(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.getBlockMaterialsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getBlockMaterialsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getSkills](context, roadmapId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getSkillsStart);
      roadmapManualApi
        .getSkills(roadmapId)
        .then((response) => {
          context.commit(mutationTypes.getSkillsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getSkillsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.addMaterials](context, {roadmapId, materials}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.addMaterialsStart);
      roadmapManualApi
        .addMaterials(roadmapId, materials)
        .then((response) => {
          context.commit(mutationTypes.addMaterialsSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.addMaterialsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.deleteMaterials](context, {roadmapId, materialIds}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.deleteMaterialsStart);
      roadmapManualApi
        .deleteMaterials(roadmapId, materialIds)
        .then((response) => {
          context.commit(mutationTypes.deleteMaterialsSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.deleteMaterialsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.updateMaterial](context, {materialId, data}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.updateMaterialStart);
      roadmapManualApi
        .updateMaterial(materialId, data)
        .then((response) => {
          context.commit(mutationTypes.updateMaterialSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.updateMaterialFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.changeMaterialStatus](context, {materialId, status}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.changeMaterialStatusStart);
      roadmapManualApi
        .changeMaterialStatus(materialId, status)
        .then((response) => {
          context.commit(mutationTypes.changeMaterialStatusSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.changeMaterialStatusFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.saveMaterialResult](context, {materialId, data}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.saveMaterialResultStart);
      roadmapManualApi
        .saveMaterialResult(materialId, data)
        .then((response) => {
          context.commit(mutationTypes.saveMaterialResultSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.saveMaterialResultFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getMaterialComments](context, materialId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getMaterialCommentsStart);
      roadmapManualApi
        .getMaterialComments(materialId)
        .then((response) => {
          context.commit(mutationTypes.getMaterialCommentsSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getMaterialCommentsFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.addMaterialComment](context, {materialId, data}) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.addMaterialCommentStart);
      roadmapManualApi
        .addMaterialComment(materialId, data)
        .then((response) => {
          context.commit(mutationTypes.addMaterialCommentSuccess);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.addMaterialCommentFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getSupervised](
    context,
    {filters = {}, pagination: {limit = 10, offset = 0}, sort: {sort_field, sort_order}}
  ) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getSupervisedStart);

      const args = {};

      if (filters.companyId) {
        args.company_id = filters.companyId;
      }
      if (filters.employeeName) {
        args.employee_name = filters.employeeName;
      }
      if (filters.users?.length) {
        args.user_ids = filters.users.map((user) => user.user_id);
      }
      if (filters.statuses?.length) {
        args.statuses = filters.statuses;
      }
      if (filters.roles?.length) {
        args.role = filters.roles;
      }
      if (filters.period?.dateStart) {
        args.start_date = getFormattedDate(filters.period.dateStart);
      }
      if (filters.period?.dateEnd) {
        args.end_date = getFormattedDate(filters.period.dateEnd);
      }
      if (typeof filters.waitingDecision === 'boolean') {
        args.waiting_decision = filters.waitingDecision;
      }

      roadmapManualApi
        .getSupervised({
          filters: args,
          pagination: {limit, offset},
          sort: {sort_field, sort_order},
        })
        .then((response) => {
          context.commit(mutationTypes.getSupervisedSuccess, {...response.data, offset});
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getSupervisedFailure, result.response.data.message);
          reject(result);
        });
    });
  },
  [actionsTypes.getSupervisedBadge](context, companyId) {
    return new Promise((resolve, reject) => {
      context.commit(mutationTypes.getSupervisedBadgeStart);
      roadmapManualApi
        .getSupervisedBadge(companyId)
        .then((response) => {
          context.commit(mutationTypes.getSupervisedBadgeSuccess, response.data);
          resolve(response.data);
        })
        .catch((result) => {
          context.commit(mutationTypes.getSupervisedBadgeFailure, result.response.data.message);
          reject(result);
        });
    });
  },
};

export default actions;
